/* eslint-disable no-control-regex */
import { Terminal } from '@xterm/xterm';
import { CanvasAddon } from '@xterm/addon-canvas';
import { FitAddon } from '@xterm/addon-fit';
import { SearchAddon } from '@xterm/addon-search';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { useEffect, useMemo, useRef, useState } from "react";
import { useEventListener } from 'usehooks-ts';
import { useContentRefresh } from "@/hooks/pages";
import { debounce, throttle } from 'throttle-debounce';

import { ChevronsDownIcon, Loader2Icon, ChevronRightIcon } from "lucide-react";
import LiveConsoleFooter from "./LiveConsoleFooter";
import LiveConsoleHeader from "./LiveConsoleHeader";
import { useAtomValue } from "jotai";
import { playerCountAtom } from "@/hooks/playerlist";
import Playerlist from "@/layout/PlayerlistSidebar/Playerlist";
import { Button } from "@/components/ui/button";
import LiveConsoleSearchBar from "./LiveConsoleSearchBar";
import LiveConsoleSaveSheet from "./LiveConsoleSaveSheet";

import ScrollDownAddon from "./ScrollDownAddon";
import terminalOptions from "./xtermOptions";
import './xtermOverrides.css';
import '@xterm/xterm/css/xterm.css';
import { getSocket } from '@/lib/utils';
import { openExternalLink } from '@/lib/navigation';
import { handleHotkeyEvent } from '@/lib/hotkeyEventListener';
import { txToast } from '@/components/TxToaster';
import { ANSI, copyTermLine, extractTermLineTimestamp, formatTermTimestamp, getNumFontVariantsLoaded } from './liveConsoleUtils';
import { getTermLineEventData, getTermLineInitialData, getTermLineRtlData, registerTermLineMarker } from './liveConsoleMarkers';


//Options
export type LiveConsoleOptions = {
    timestampDisabled: boolean;
    timestampForceHour12: boolean | undefined;
    copyTimestamp: boolean;
    copyTag: boolean;
}

//Loading local storage configs
//FIXME: this is hacky, maybe use atomWithStorage
let timestampDisabled = false;
let timestampForceHour12: boolean | undefined = undefined;
try {
    const localConfig = localStorage.getItem('liveConsoleTimestamp');
    if (localConfig === '24h') {
        timestampForceHour12 = false;
    } else if (localConfig === '12h') {
        timestampForceHour12 = true;
    } else if (localConfig === 'off') {
        timestampDisabled = true;
    }
} catch (error) { }
let copyTimestamp = false;
let copyTag = true;
try {
    const localConfig = localStorage.getItem('liveConsoleCopyOpts');
    if (typeof localConfig === 'string') {
        const parts = localConfig.split(',');
        copyTimestamp = parts.includes('ts');
        copyTag = parts.includes('tag');
    }
} catch (error) { }


//Consts
const keyDebounceTime = 150; //ms

//FIXME: move to inside the component
const defaultTermPrefix = formatTermTimestamp(
    Date.now(),
    {
        timestampDisabled,
        timestampForceHour12,
        copyTimestamp,
        copyTag,
    }
).replace(/\w/g, '-');

//Main component
export default function LiveConsolePage() {
    const [isSaveSheetOpen, setIsSaveSheetOpen] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
        const [showSearchBar, setShowSearchBar] = useState(false);
    const [isPlayerListOpen, setIsPlayerListOpen] = useState(() => {
        return localStorage.getItem('liveConsoleSidebarOpen') !== 'false';
    });
    const togglePlayerList = (open: boolean) => {
        setIsPlayerListOpen(open);
        localStorage.setItem('liveConsoleSidebarOpen', String(open));
    };
    const playerCount = useAtomValue(playerCountAtom);
    const termInputRef = useRef<HTMLInputElement>(null);
    const termPrefixRef = useRef({
        ts: 0, //so we can clear the console
        lastEol: true,
        //FIXME: defaultTermPrefix depends on options, deal with it when options change
        prefix: defaultTermPrefix,
    });
    const refreshPage = useContentRefresh();

    //FIXME: maybe use atomWithStorage
    const consoleOptions: LiveConsoleOptions = useMemo(() => {
        return {
            timestampDisabled,
            timestampForceHour12,
            copyTimestamp,
            copyTag,
        };
    }, []);


    /**
     * xterm stuff
     */
    const jumpBottomBtnRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const term = useMemo(() => new Terminal(terminalOptions), []);
    const fitAddon = useMemo(() => new FitAddon(), []);
    const searchAddon = useMemo(() => new SearchAddon(), []);
    const termLinkHandler = (event: MouseEvent, uri: string) => {
        openExternalLink(uri);
    };
    const webLinksAddon = useMemo(() => new WebLinksAddon(termLinkHandler), []);

    const sendSearchKeyEvent = throttle(keyDebounceTime, (action: string) => {
        window.postMessage({
            type: 'liveConsoleSearchHotkey',
            action,
        });
    }, { noTrailing: true });

    const refitTerminal = () => {
        if (!containerRef.current || !term.element || !fitAddon) {
            console.log('refitTerminal: no containerRef.current or term.element or fitAddon');
            return;
        }

        const proposed = fitAddon.proposeDimensions();
        if (proposed) {
            term.resize(proposed.cols, proposed.rows);
        } else {
            console.log('refitTerminal: no proposed dimensions');
        }
    }
    useEventListener('resize', debounce(100, refitTerminal));

    useEffect(() => {
        if (containerRef.current && jumpBottomBtnRef.current && !term.element) {
            console.log('live console xterm init');
            containerRef.current.innerHTML = ''; //due to HMR, the terminal element might still be there
            term.loadAddon(fitAddon);
            term.loadAddon(searchAddon);
            term.loadAddon(webLinksAddon);
            term.loadAddon(new CanvasAddon());
            term.loadAddon(new ScrollDownAddon(jumpBottomBtnRef.current, containerRef.current));
            term.open(containerRef.current);
            term.write('\x1b[?25l'); //hide cursor
            refitTerminal();

            const scrollPageUp = throttle(keyDebounceTime, () => {
                term.scrollLines(Math.min(1, 2 - term.rows));
            }, { noTrailing: true });
            const scrollPageDown = throttle(keyDebounceTime, () => {
                term.scrollLines(Math.max(1, term.rows - 2));
            }, { noTrailing: true });
            const scrollTop = throttle(keyDebounceTime, () => {
                term.scrollToTop();
            }, { noTrailing: true });
            const scrollBottom = throttle(keyDebounceTime, () => {
                term.scrollToBottom();
            }, { noTrailing: true });

            term.attachCustomKeyEventHandler((e: KeyboardEvent) => {
                // Some are handled by the live console element
                if (e.code === 'F5' && !e.ctrlKey) {
                    return false;
                } else if (e.code === 'Escape') {
                    return false;
                } else if (e.code === 'KeyF' && (e.ctrlKey || e.metaKey)) {
                    return false;
                } else if (e.code === 'F3') {
                    return false;
                } else if (e.code === 'KeyC' && (e.ctrlKey || e.metaKey)) {
                    const selection = term.getSelection();
                    if (!selection) return false;
                    copyTermLine(
                        selection,
                        term.element as any,
                        consoleOptions,
                        termInputRef.current
                    ).then((res) => {
                        //undefined if no error
                        if (res === false) {
                            txToast.error('Failed to copy to clipboard :(');
                        }
                    }).catch((error) => {
                        txToast.error({
                            title: 'Failed to copy to clipboard:',
                            msg: error.message,
                        });
                    });
                    term.clearSelection();
                    return false;
                } else if (e.code === 'PageUp') {
                    scrollPageUp();
                    return false;
                } else if (e.code === 'PageDown') {
                    scrollPageDown();
                    return false;
                } else if (e.code === 'Home') {
                    scrollTop();
                    return false;
                } else if (e.code === 'End') {
                    scrollBottom();
                    return false;
                } else if (handleHotkeyEvent(e)) {
                    return false;
                }
                return true;
            });
        }
    }, [term]);

    useEventListener('keydown', (e: KeyboardEvent) => {
        if (e.code === 'F5' && !e.ctrlKey) {
            if (isConnected) {
                refreshPage();
                e.preventDefault();
            }
        } else if (e.code === 'Escape') {
            searchAddon.clearDecorations();
            setShowSearchBar(false);
        } else if (e.code === 'KeyF' && (e.ctrlKey || e.metaKey)) {
            if (showSearchBar) {
                sendSearchKeyEvent('focus');
            } else {
                setShowSearchBar(true);
            }
            e.preventDefault();
        } else if (e.code === 'F3') {
            sendSearchKeyEvent(e.shiftKey ? 'previous' : 'next');
            e.preventDefault();
        }
    });

    //NOTE: quickfix for https://github.com/xtermjs/xterm.js/issues/4994
    const writeToTerminal = (data: string) => {
        const lines = data.split(/\r?\n/);
        //check if last line isn't empty
        // NOTE: i'm not trimming because having multiple \n at the end is valid
        let wasEolStripped = false;
        if (lines.length && !lines[lines.length - 1]) {
            lines.pop();
            wasEolStripped = true;
        }

        //extracts timestamp & print each line
        let isNewTs = false;
        for (let i = 0; i < lines.length; i++) {
            isNewTs = false;
            let line = lines[i];
            //tries to extract timestamp
            try {
                const { ts, content } = extractTermLineTimestamp(line);
                if (ts) {
                    isNewTs = true;
                    line = content;
                    termPrefixRef.current.ts = ts;
                    termPrefixRef.current.prefix = formatTermTimestamp(ts, consoleOptions);
                }
            } catch (error) {
                termPrefixRef.current.prefix = defaultTermPrefix;
                console.warn('Failed to parse timestamp from:', line, (error as any).message);
            }

            //Markers
            let writeCallback: (() => void) | undefined;
            try {
                const res = getTermLineEventData(line)
                    ?? getTermLineInitialData(line)
                    ?? getTermLineRtlData(line); //https://github.com/xtermjs/xterm.js/issues/701
                if (res && res.markerData) {
                    writeCallback = () => registerTermLineMarker(term, res.markerData);
                }
                if (res && res.newLine) {
                    line = res.newLine;
                }
            } catch (error) {
                console.error('Failed to process marker:', (error as any).message);
            }

            //Check if it's last line, and if the EOL was stripped
            const prefixColor = isNewTs ? ANSI.WHITE : ANSI.GRAY;
            const prefix = termPrefixRef.current.lastEol
                ? prefixColor + termPrefixRef.current.prefix
                : '';
            if (i < lines.length - 1) {
                term.writeln(prefix + line, writeCallback);
                termPrefixRef.current.lastEol = true;
            } else {
                if (wasEolStripped) {
                    term.writeln(prefix + line, writeCallback);
                    termPrefixRef.current.lastEol = true;
                } else {
                    term.write(prefix + line, writeCallback);
                    termPrefixRef.current.lastEol = false;
                }
            }
        }
    }

    //DEBUG
    // useEffect(() => {
    //     let cnt = 0;
    //     const interval = setInterval(function LoLoLoLoLoLoL() {
    //         cnt++;
    //         const mod = cnt % 60;
    //         term.writeln(
    //             cnt.toString().padStart(6, '0') + ' ' +
    //             '\u001b[1m\u001b[31m=\u001b[0m'.repeat(mod) +
    //             '\u001b[1m\u001b[33m.\u001b[0m'.repeat(60 - mod)
    //         );
    //     }, 100);
    //     return () => clearInterval(interval);
    // }, []);


    /**
     * SocketIO stuff
     */
    const socketStateChangeCounter = useRef(0);
    const pageSocket = useRef<ReturnType<typeof getSocket> | null>(null);

    //Runing on mount only
    useEffect(() => {
        pageSocket.current = getSocket(['liveconsole']);
        pageSocket.current.on('connect', () => {
            console.log("LiveConsole Socket.IO Connected.");
            setIsConnected(true);
        });
        pageSocket.current.on('disconnect', (message) => {
            console.log("LiveConsole Socket.IO Disonnected:", message);
            //Grace period of 500ms to allow for quick reconnects
            //Tracking the state change ID for the timeout not to overwrite a reconnection
            const newId = socketStateChangeCounter.current + 1;
            socketStateChangeCounter.current = newId;
            setTimeout(() => {
                if (socketStateChangeCounter.current === newId) {
                    setIsConnected(false);
                }
            }, 500);
        });
        pageSocket.current.on('error', (error) => {
            console.log('LiveConsole Socket.IO', error);
        });
        pageSocket.current.on('consoleData', function (data) {
            writeToTerminal(data);
        });

        return () => {
            pageSocket.current?.removeAllListeners();
            pageSocket.current?.disconnect();
        }
    }, []);


    //Font loading effect
    //NOTE: on first render, the font might not be loaded yet, in this case we listen for the loadingdone event
    //  and refresh the page when it happens, so the terminal can be properly rendered
    //  Ref: https://github.com/xtermjs/xterm.js/issues/5164
    //  This is a bad solution, we should first check if loaded, and _then_ render the terminal, with timeout if needed
    useEffect(() => {
        if (getNumFontVariantsLoaded('--font-mono', 'INITIAL EFFECT')) return; //we don't need to await anything
        const handleFontLoadingDone = () => {
            getNumFontVariantsLoaded('--font-mono', 'ON FONT LOADING DONE');
            refreshPage();
        };
        document.fonts.addEventListener('loadingdone', handleFontLoadingDone);
        return () => {
            document.fonts.removeEventListener('loadingdone', handleFontLoadingDone);
        };
    }, []);


    /**
     * Action Handlers
     */
    const consoleWrite = (cmd: string) => {
        if (!isConnected || !pageSocket.current) return;
        if (cmd === 'cls' || cmd === 'clear') {
            clearConsole();
        } else {
            pageSocket.current.emit('consoleCommand', cmd);
        }
    }
    const clearConsole = () => {
        term.clear();
        searchAddon.clearDecorations();
        setShowSearchBar(false);
        term.write(`${ANSI.YELLOW}[console cleared]${ANSI.RESET}\n`);
    }
    const toggleSearchBar = () => {
        setShowSearchBar(!showSearchBar);
    }
    const toggleSaveSheet = () => {
        setIsSaveSheetOpen(!isSaveSheetOpen);
    }
    const inputSuggestions = (cmd: string) => {
        if (termInputRef.current) {
            termInputRef.current.value = cmd;
            termInputRef.current.focus();
        }
        setIsSaveSheetOpen(false);
    }


    return (
        <div className="dark text-primary flex flex-col h-contentvh w-full bg-[#030507] border border-white/[0.06] md:rounded-xl overflow-clip shadow-[0_0_50px_rgba(0,255,209,0.04)]">
            <LiveConsoleHeader 
                isPlayerListOpen={isPlayerListOpen}
                setIsPlayerListOpen={togglePlayerList}
                playerCount={playerCount}
                isConnected={isConnected}
            />

            <div className="flex flex-row relative grow overflow-hidden">
                {/* Connecting overlay */}
                {!isConnected ? (
                    <div className='absolute inset-0 z-20 bg-[#030507]/80 flex items-center justify-center backdrop-blur-sm'>
                        <div className='flex flex-col gap-5 items-center justify-center select-none'>
                            <div className="relative">
                                <Loader2Icon className='w-12 h-12 animate-spin text-[#00ffd1]/60' />
                                <div className="absolute inset-0 rounded-full blur-xl bg-[#00ffd1]/10" />
                            </div>
                            <div className="flex flex-col items-center gap-1">
                                <span className='text-base font-mono font-semibold tracking-widest uppercase text-slate-300'>
                                    Connecting console...
                                </span>
                                <span className='text-[10px] font-mono text-slate-600 tracking-wider'>
                                    waiting for socket
                                </span>
                            </div>
                        </div>
                    </div>
                ) : null}

                <LiveConsoleSaveSheet
                    isOpen={isSaveSheetOpen}
                    closeSheet={() => setIsSaveSheetOpen(false)}
                    toTermInput={(cmd) => inputSuggestions(cmd)}
                />

                {/* Left Area: Console & Tools */}
                <div className="flex flex-col relative flex-1 overflow-hidden h-full">
                    {/* Quick Command Strip */}
                    <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 bg-black/20 border-b border-white/[0.04] select-none">
                        <span className="text-[9px] uppercase font-bold tracking-[0.2em] text-slate-600 mr-1 font-mono">Quick:</span>
                        {[
                            { label: 'clear',   cmd: 'cls',      color: 'hover:border-[#00ff66]/25 hover:text-[#00ff66]' },
                            { label: 'refresh', cmd: 'refresh',  color: 'hover:border-[#00ffd1]/25 hover:text-[#00ffd1]' },
                            { label: 'status',  cmd: 'status',   color: 'hover:border-[#a78bfa]/25 hover:text-[#a78bfa]' },
                            { label: 'info',    cmd: 'txadmin',  color: 'hover:border-[#f59e0b]/25 hover:text-[#f59e0b]' },
                        ].map(({ label, cmd, color }) => (
                            <button
                                key={cmd}
                                onClick={() => consoleWrite(cmd)}
                                className={`px-2.5 py-1 rounded-md border border-white/[0.05] bg-white/[0.02] text-slate-500 font-mono text-[10px] font-semibold tracking-wider transition-all duration-150 ${color} hover:bg-white/[0.04]`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* Terminal container */}
                    <div className="flex-1 relative w-full h-full bg-[#030507]">
                        <div ref={containerRef} className='absolute top-1 left-2 right-0 bottom-0' />
                    </div>

                    {/* Search bar */}
                    <LiveConsoleSearchBar
                        show={showSearchBar}
                        setShow={setShowSearchBar}
                        searchAddon={searchAddon}
                    />

                    {/* Scroll to bottom */}
                    <button
                        ref={jumpBottomBtnRef}
                        className='absolute bottom-0 right-2 z-10 hidden opacity-75'
                        onClick={() => { term.scrollToBottom() }}
                    >
                        <ChevronsDownIcon className='w-12 h-12 animate-pulse hover:animate-none hover:scale-110 text-[#00ffd1]/70' />
                    </button>
                </div>

                {/* Right Area: Collapsible Player list sidebar */}
                {isPlayerListOpen && (
                    <div className="hidden lg:flex w-80 flex-col border-l border-[#1f2d4d] bg-[#0c1322]/90 h-full animate-in slide-in-from-right duration-300 shrink-0 select-none">
                        <div className="p-4 border-b border-[#1f2d4d] bg-[#0a0f1d] flex items-center justify-between">
                            <div>
                                <h4 className="text-sm font-bold text-slate-200">Online Players</h4>
                                <p className="text-[10px] text-slate-500">Live active session</p>
                            </div>
                            <Button 
                                variant="ghost" 
                                size="xs" 
                                className="h-7 w-7 text-slate-400 hover:text-slate-200 hover:bg-[#121b2f] rounded-full"
                                onClick={() => togglePlayerList(false)}
                            >
                                <ChevronRightIcon className="h-4 w-4" />
                            </Button>
                        </div>
                        {/* Player Slots Capacity indicator */}
                        <div className="p-4 border-b border-[#1f2d4d]/60 bg-[#0c1322]/50">
                            <div className="flex items-center justify-between text-2xs uppercase tracking-wider font-bold text-slate-400 mb-1.5">
                                <span>Slots filled</span>
                                <span>{playerCount} / 128</span>
                            </div>
                            <div className="w-full h-1.5 bg-slate-900 rounded-full overflow-hidden border border-[#1f2d4d]/30">
                                <div 
                                    className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full transition-all duration-500 shadow-[0_0_8px_rgba(6,182,212,0.6)]" 
                                    style={{ width: `${Math.min(100, (playerCount / 128) * 100)}%` }}
                                />
                            </div>
                        </div>
                        <div className="flex-grow overflow-hidden flex flex-col bg-[#070b13]/20">
                            <Playerlist />
                        </div>
                    </div>
                )}
            </div>

            <LiveConsoleFooter
                termInputRef={termInputRef}
                isConnected={isConnected}
                consoleWrite={consoleWrite}
                consoleClear={clearConsole}
                toggleSaveSheet={toggleSaveSheet}
                toggleSearchBar={toggleSearchBar}
            />
        </div>
    )
}
